import { z } from 'zod';
import type { operations } from '@molchanovs/api-contract';
import { api } from './client';
import { ApiError } from './errors';
import {
  getBubbleMutationOperation,
  type BubbleMutationOperation,
} from './bubble-mutation-operation';

export type ArticleListQuery = NonNullable<operations['getApiV1Articles']['parameters']['query']>;
export type ArticleListResponse =
  operations['getApiV1Articles']['responses'][200]['content']['application/json'];
export type ArticleResponse = Extract<
  operations['getApiV1ArticlesByArticleId']['responses'][200]['content']['application/json'],
  { data: { title: string | null } }
>;
export type Article = ArticleResponse['data'];
export type ArticleListItem = ArticleListResponse['data']['items'][number];
export type ArticleDetail = ArticleResponse & { etag: string };
export type CreateArticleBody =
  operations['postApiV1Articles']['requestBody']['content']['application/json'];
export type UpdateArticleBody = NonNullable<
  operations['patchApiV1ArticlesByArticleId']['requestBody']
>['content']['application/merge-patch+json'];
export type ArticleWriteResponse =
  operations['postApiV1Articles']['responses'][201]['content']['application/json'];
export type ArticleDeleteResponse =
  operations['deleteApiV1ArticlesByArticleId']['responses'][202]['content']['application/json'];
export type ArticleTopicsResponse =
  operations['getApiV1ArticleTopics']['responses'][200]['content']['application/json'];
export type ArticleOperation = BubbleMutationOperation;

const statusSchema = z.enum(['Draft', 'Approved']);
const strongEtagSchema = z.string().regex(/^"[\x21\x23-\x7e]+"$/u);
const keySchema = z.string().min(8).max(200);
const idSchema = z.uuid();
const querySchema: z.ZodType<ArticleListQuery> = z
  .object({
    page: z.number().int().min(1).max(100000).optional(),
    pageSize: z.number().int().min(1).max(100).optional(),
    q: z.string().trim().min(1).max(200).optional(),
    status: statusSchema.optional(),
    topicId: idSchema.optional(),
  })
  .strict();
const fields = {
  title: z
    .string()
    .trim()
    .min(1)
    .max(500)
    .refine((value) => !value.includes('\u0000')),
  content: z
    .string()
    .min(1)
    .max(100000)
    .refine((value) => !value.includes('\u0000')),
  topicId: idSchema.nullable().optional(),
  status: statusSchema,
};
const createSchema: z.ZodType<CreateArticleBody> = z
  .object({ ...fields, status: statusSchema.optional() })
  .strict();
const updateSchema: z.ZodType<UpdateArticleBody> = z
  .object({ ...fields, replacementDecision: z.enum(['replace', 'keep_existing']).optional() })
  .partial()
  .strict()
  .refine((body) =>
    [body.title, body.content, body.topicId, body.status].some((value) => value !== undefined)
  );
const listItemSchema = z.object({
  id: idSchema,
  bubbleId: z.string().nullable(),
  title: z.string().nullable(),
  status: z.string().nullable(),
  topic: z.object({ id: idSchema, dbValue: z.string(), display: z.string() }).nullable(),
  createdAt: z.iso.datetime({ offset: true }).nullable(),
  updatedAt: z.iso.datetime({ offset: true }).nullable(),
  deletion: z.object({ state: z.enum(['pending', 'failed']), operationId: idSchema }).nullable(),
});
const listSchema: z.ZodType<ArticleListResponse> = z.object({
  data: z.object({
    items: z.array(
      listItemSchema.extend({
        relatedTask: z.object({ id: idSchema, number: z.string().nullable() }).nullable(),
      })
    ),
  }),
  meta: z.object({
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    totalItems: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    knowledge: z.object({
      state: z.enum(['unavailable', 'empty', 'pending', 'ready', 'failed', 'unknown']),
      current: z.boolean(),
      eligibleCount: z.number().int().nonnegative().nullable(),
      readyCount: z.number().int().nonnegative().nullable(),
      cleanupPending: z.boolean().nullable(),
      errorCode: z
        .enum([
          'configuration_unavailable',
          'state_unavailable',
          'source_unavailable',
          'upload_failed',
          'upload_unconfirmed',
          'attach_failed',
          'attach_unconfirmed',
          'indexing_failed',
          'detach_failed',
          'detach_unconfirmed',
        ])
        .nullable(),
    }),
  }),
});
const detailSchema: z.ZodType<ArticleResponse> = z.object({
  data: listItemSchema.extend({
    content: z.string().nullable(),
    generation: z
      .object({
        operationId: idSchema,
        taskUuid: idSchema,
        candidateArticleId: idSchema.nullable(),
        candidateRevision: z.string().nullable(),
      })
      .nullable()
      .optional(),
  }),
});
const writeSchema: z.ZodType<ArticleWriteResponse> = z.object({ data: z.object({ id: idSchema }) });
const deleteSchema: z.ZodType<ArticleDeleteResponse> = z.object({
  data: z.object({ operationId: idSchema, status: z.literal('accepted'), monitorUrl: z.string() }),
});
const topicsSchema: z.ZodType<ArticleTopicsResponse> = z.object({
  data: z.object({
    topics: z
      .array(z.object({ id: idSchema, dbValue: z.string().min(1), display: z.string().min(1) }))
      .max(200),
  }),
});

function parseInput<T>(schema: z.ZodType<T>, input: unknown): T {
  const result = schema.safeParse(input);
  if (!result.success)
    throw new ApiError({
      message: 'Invalid Article request.',
      status: 0,
      code: 'VALIDATION_ERROR',
      details: result.error.issues,
    });
  return result.data;
}

function articlePath(id: string) {
  return `/api/v1/articles/${encodeURIComponent(parseInput(idSchema, id))}`;
}
function writeHeaders(key: string) {
  return { 'Idempotency-Key': parseInput(keySchema, key) };
}
function conditionalHeaders(etag: string, key: string) {
  return { ...writeHeaders(key), 'If-Match': parseInput(strongEtagSchema, etag) };
}

/** Stateless transport facade. The caller owns each write key and explicit replay policy. */
export const articlesApi = {
  async topics(signal?: AbortSignal): Promise<ArticleTopicsResponse> {
    return api.get('/api/v1/articles/topics', { signal, schema: topicsSchema });
  },
  async list(query: ArticleListQuery, signal?: AbortSignal): Promise<ArticleListResponse> {
    return api.get('/api/v1/articles', {
      params: parseInput(querySchema, query),
      signal,
      schema: listSchema,
    });
  },
  async detail(id: string, signal?: AbortSignal): Promise<ArticleDetail> {
    const response = await api.getWithMetadata<ArticleResponse>(articlePath(id), {
      signal,
      schema: detailSchema,
    });
    const etag = strongEtagSchema.safeParse(response.headers.get('ETag'));
    if (!etag.success)
      throw new ApiError({
        message: 'Article response is missing a valid strong ETag.',
        status: response.status,
        code: 'VALIDATION_ERROR',
        details: etag.error.issues,
      });
    return { ...response.data, etag: etag.data };
  },
  async create(
    body: CreateArticleBody,
    key: string,
    signal?: AbortSignal
  ): Promise<ArticleWriteResponse> {
    return api.post('/api/v1/articles', parseInput(createSchema, body), {
      headers: writeHeaders(key),
      signal,
      schema: writeSchema,
    });
  },
  async update(
    id: string,
    body: UpdateArticleBody,
    etag: string,
    key: string,
    signal?: AbortSignal
  ): Promise<ArticleWriteResponse> {
    return api.patch(articlePath(id), parseInput(updateSchema, body), {
      headers: { ...conditionalHeaders(etag, key), 'Content-Type': 'application/merge-patch+json' },
      signal,
      schema: writeSchema,
    });
  },
  async delete(
    id: string,
    etag: string,
    key: string,
    signal?: AbortSignal
  ): Promise<ArticleDeleteResponse> {
    return api.delete(articlePath(id), {
      headers: conditionalHeaders(etag, key),
      signal,
      schema: deleteSchema,
    });
  },
  operation: getBubbleMutationOperation,
};
